/**
 * @file       pages/historypage.jsx
 * @author     Alex Huctwith <alex@kingsds.network>
 * @author     Kevin Yu <kevin@distributive.network>
 *
 * @date       May 2024
 *
 *             Portal page for displaying and managing account history
 */

import { BankOperationTabs } from '../components/BankOperationTabs';
import { HistoryFilters } from '../components/HistoryFilters/HistoryFilters';
import { HistoryHeader } from '../components/HistoryHeader/HistoryHeader';
import { Layout } from '@/components/Layout';
import { Transactions } from '../components/Transactions/Transactions';

import './HistoryPage.css';
import { useContext, useEffect } from 'react';
import { HistoryContext } from '../stores/HistoryContext';
import { AccountsContext } from '../stores/AccountsContext';
import { useNavigate, useSearchParams } from 'react-router-dom';

/**
 *
 * @param   {object}      _props Component properties.
 * @returns {JSX.Element}        Component that renders the transaction/history page.
 */
export function HistoryPage(_props) {
  const {
    getTransactions,
    setStartDate,
    setEndDate,
    setTransactionType,
    setFilteredTransactions,
    setSelectedBankAccount,
  } = useContext(HistoryContext);
  const accounts = useContext(AccountsContext);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  // Essential to fetch transactions when user navigates from accounts to transactions page
  useEffect(() => {
    // If there's no selected account set the first account <option> as the default
    if (searchParams.get('account'))
    {
      const bankAccount = accounts.find((account) => account.label === searchParams.get('account'));
      if (!bankAccount.unlocked) 
        navigate('/accounts');
      setSelectedBankAccount(bankAccount);
    }
    else
    {
      navigate('/accounts');
    } 

    getTransactions();

    // Reset the history filter options when component dismounts
    return () => {
      setStartDate('');
      setEndDate('');
      setTransactionType('');
      setFilteredTransactions([]);
    };
  }, [
    accounts,
    getTransactions,
    setEndDate,
    setFilteredTransactions,
    setSelectedBankAccount,
    setStartDate,
    setTransactionType,
    searchParams,
    navigate,
  ]);

  return (
    <Layout contentClassName="history-page" mobileHeader="History">
      <div className="history-page-content">
        <HistoryHeader />
        <HistoryFilters />
        <Transactions />
      </div>
      <BankOperationTabs />
    </Layout>
  );
}
